import { useTranslation } from 'react-i18next';
import { formatPrice } from 'utils/helpers/functions/formatPrice';
import {
    getBaseCurrencyCode,
    getCurrencyCode,
    withBaseCurrency,
    withLocalCurrency,
} from 'utils/helpers/currency/currencyHelpers';
import SolarIcon from 'components/icons/SolarIcon';
import type { AnalyticsDashPayments } from '../model';

interface DashPaymentsProps {
    isUahMode: boolean;
    payments: AnalyticsDashPayments;
}

const payTone = (payments: AnalyticsDashPayments) => {
    if (!payments.hasPayments) return 'empty';
    return payments.paymentsCovered ? 'covered' : 'partial';
};

const DashPayments = ({ isUahMode, payments }: DashPaymentsProps) => {
    const { t } = useTranslation(['Objects']);
    const canToggleDebt =
        payments.onToggleDebtCurrency != null && payments.debtAmountLocal != null;
    const showLocalDebt = canToggleDebt && payments.debtCurrency === 'local';
    const debtShown = showLocalDebt ? Number(payments.debtAmountLocal) : payments.debtAmount;
    const debtText =
        isUahMode || showLocalDebt
            ? withLocalCurrency(formatPrice(debtShown))
            : withBaseCurrency(formatPrice(debtShown));

    const debtRow = (
        <>
            <span className="text-danger">{t('labelDebt')}</span>
            <span className="text-danger fw-medium">{debtText}</span>
        </>
    );

    return (
        <div className={`object-analytics-dash__pay object-analytics-dash__pay--${payTone(payments)}`}>
            <div className="object-analytics-dash__pay-head">
                <span className="object-analytics-dash__tile-label object-analytics-dash__label-with-icon text-muted">
                    <SolarIcon name="wallet2" aria-hidden="true" />
                    {t('infoItems.objectPayments')}
                </span>
                {payments.hasPayments ? (
                    <span
                        className={`object-analytics-dash__pay-status ${
                            payments.paymentsCovered ? 'text-success' : 'text-warning'
                        }`}
                    >
                        <SolarIcon name={payments.statusIcon} aria-hidden="true" />
                        {payments.statusLabel}
                    </span>
                ) : (
                    <span className="text-muted">{t('infoItems.objectPaymentsEmpty')}</span>
                )}
            </div>
            {payments.hasPayments ? (
                <div className="object-analytics-dash__pay-sums">
                    {payments.paidUsd !== 0 ? (
                        <span title={getBaseCurrencyCode()}>{withBaseCurrency(formatPrice(payments.paidUsd))}</span>
                    ) : null}
                    {payments.paidUah !== 0 ? (
                        <span title={getCurrencyCode()}>{withLocalCurrency(formatPrice(payments.paidUah))}</span>
                    ) : null}
                </div>
            ) : null}
            <div className="object-analytics-dash__pay-meta">
                <span className="text-muted">{t('infoItems.objectPaymentsCoverage')}</span>
                <span className="object-analytics-dash__pay-pct fw-medium text-body">{payments.coveragePercent}%</span>
            </div>
            <div
                className="object-analytics-dash__track"
                role="progressbar"
                aria-valuenow={payments.coveragePercent}
                aria-valuemin={0}
                aria-valuemax={100}
            >
                <div className="object-analytics-dash__fill" style={{ width: `${payments.coveragePercent}%` }} />
            </div>
            {payments.debtAmount > 0 ? (
                canToggleDebt ? (
                    <button
                        type="button"
                        className="object-analytics-dash__debt object-analytics-dash__debt-toggle"
                        onClick={payments.onToggleDebtCurrency}
                    >
                        {debtRow}
                    </button>
                ) : (
                    <div className="object-analytics-dash__debt">{debtRow}</div>
                )
            ) : null}
        </div>
    );
};

export default DashPayments;
